import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import { Button, Row, Col, Table, Input, Label, ButtonGroup } from "reactstrap";
import axios from "axios";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import { TableHeader, Pagination, Search } from "../../../DataTable";
import useFullPageLoader from "../../../hooks/useFullPageLoader";
import { puede } from "../../../../permisos/permisos";

// Reporte de minimos: inventario actual contra minimo/maximo por articulo (PT)
// y por materia prima (resinas).
function ReporteMinimos() {
  const { user } = useContext(AuthContext);
  const URL_INVENTARIOS = process.env.REACT_APP_URL_INVENTARIOS;
  const headers = { headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } };

  const [productoTerminado, setProductoTerminado] = useState([]);
  const [materiaPrima, setMateriaPrima] = useState([]);
  // "" = todos | "bajo" = solo bajo minimo
  const [filtroEstado, setFiltroEstado] = useState("bajo");
  const [seccion, setSeccion] = useState("PT");

  //--- Pagination
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState({ field: "", order: "" });
  const ITEMS_PER_PAGE = 50;

  useEffect(() => {
    showLoader();
    axios
      .get(`${URL_INVENTARIOS}ReporteMinimos`, headers)
      .then((r) => {
        setProductoTerminado(r.data.productoTerminado || []);
        setMateriaPrima(r.data.materiaPrima || []);
        hideLoader();
      })
      .catch((e) => {
        console.log(e);
        hideLoader();
      });
  }, []);

  const headersPT = [
    { name: "Código", field: "codigo", sortable: true },
    { name: "Artículo", field: "descripcion", sortable: true },
    { name: "Inventario", field: "existencia", sortable: true },
    { name: "Mínimo", field: "minimo", sortable: true },
    { name: "Máximo", field: "maximo", sortable: true },
    { name: "Faltante", field: "faltante", sortable: true },
    { name: "Estado", field: "estado", sortable: false },
  ];

  const headersMP = [
    { name: "Materia Prima", field: "nombre", sortable: true },
    { name: "Color", field: "color", sortable: true },
    { name: "Inventario", field: "existencia", sortable: true },
    { name: "Mínimo", field: "minimo", sortable: true },
    { name: "Faltante", field: "faltante", sortable: true },
    { name: "Estado", field: "estado", sortable: false },
  ];

  const camposNumericos = ["existencia", "minimo", "maximo", "faltante"];

  const commentsData = useMemo(() => {
    let cc = (seccion === "PT" ? productoTerminado : materiaPrima)
      .filter((a) => a.minimo > 0 || a.existencia > 0)
      .filter((a) => (filtroEstado === "bajo" ? a.existencia < a.minimo : true));

    if (search) {
      const s = search.toLowerCase();
      cc = cc.filter((c) =>
        seccion === "PT"
          ? (c.codigo || "").toString().toLowerCase().includes(s) ||
            (c.descripcion || "").toLowerCase().includes(s)
          : (c.nombre || "").toLowerCase().includes(s) ||
            (c.color || "").toLowerCase().includes(s)
      );
    }

    setTotalItems(cc.length);

    if (sorting.field && !camposNumericos.includes(sorting.field)) {
      const reversed = sorting.order === "asc" ? 1 : -1;
      cc = [...cc].sort(
        (a, b) => reversed * (a[sorting.field] || "").toString().localeCompare((b[sorting.field] || "").toString())
      );
    }
    if (sorting.field && camposNumericos.includes(sorting.field)) {
      const reversed = sorting.order === "asc" ? 1 : -1;
      cc = [...cc].sort((a, b) => reversed * ((parseFloat(a[sorting.field]) || 0) - (parseFloat(b[sorting.field]) || 0)));
    }
    if (!sorting.field) {
      // Por defecto: los mas faltantes primero.
      cc = [...cc].sort((a, b) => (b.faltante || 0) - (a.faltante || 0));
    }

    return cc.slice((currentPage - 1) * ITEMS_PER_PAGE, (currentPage - 1) * ITEMS_PER_PAGE + ITEMS_PER_PAGE);
  }, [productoTerminado, materiaPrima, seccion, filtroEstado, search, sorting, currentPage]);

  // Todas las filas filtradas (sin paginar) para PDF/Excel.
  function filasFiltradas() {
    let cc = (seccion === "PT" ? productoTerminado : materiaPrima)
      .filter((a) => a.minimo > 0 || a.existencia > 0)
      .filter((a) => (filtroEstado === "bajo" ? a.existencia < a.minimo : true));
    return [...cc].sort((a, b) => (b.faltante || 0) - (a.faltante || 0));
  }

  const badge = (a) =>
    a.existencia < a.minimo ? (
      <span className="badge badge-danger">Bajo mínimo</span>
    ) : a.maximo > 0 && a.existencia > a.maximo ? (
      <span className="badge badge-warning">Sobre máximo</span>
    ) : (
      <span className="badge badge-success">OK</span>
    );

  function PDFTabla() {
    const doc = new jsPDF();
    var img = new Image();
    img.src = process.env.PUBLIC_URL + "Logo-Lynqro-PDF.png";
    doc.addImage(img, "png", 150, 10, 45, 15);
    doc.setFontSize(14);
    doc.text("Reporte de Mínimos de Inventario", 14, 18);
    doc.setFontSize(11);
    if (seccion === "PT") {
      doc.autoTable({
        head: [["Código", "Artículo", "Inventario", "Mínimo", "Máximo", "Faltante"]],
        body: filasFiltradas().map((a) => [a.codigo, a.descripcion, a.existencia, a.minimo, a.maximo, a.faltante]),
        startY: 30,
        styles: { fontSize: 9 },
      });
    } else {
      doc.autoTable({
        head: [["Materia Prima", "Color", "Inventario", "Mínimo", "Faltante"]],
        body: filasFiltradas().map((a) => [a.nombre, a.color, a.existencia, a.minimo, a.faltante]),
        startY: 30,
        styles: { fontSize: 9 },
      });
    }
    window.open(doc.output("bloburl"), "_blank");
  }

  function excel() {
    const data =
      seccion === "PT"
        ? filasFiltradas().map((a) => ({ Codigo: a.codigo, Articulo: a.descripcion, Inventario: a.existencia, Minimo: a.minimo, Maximo: a.maximo, Faltante: a.faltante }))
        : filasFiltradas().map((a) => ({ MateriaPrima: a.nombre, Color: a.color, Inventario: a.existencia, Minimo: a.minimo, Faltante: a.faltante }));
    new ExportJsonExcel({
      fileName: "ReporteMinimos",
      datas: [{ sheetData: data, sheetName: "Minimos" }],
    }).saveExcel();
  }

  return (
    <>
      <Header />
      <br />
      <div className="container">
        {puede(user, "inventarios.minimos.ver") || puede(user, "inventarios.minimos.ver") || puede(user, "inventarios.minimos.ver") ? (
          <div className="card col-12">
            <div className="page-header">
              <div>
                <h3 className="page-title">
                  <i className="fas fa-exclamation-triangle" style={{ marginRight: 8, color: "#ef4444" }} />
                  Reporte de Mínimos
                </h3>
                <p className="page-subtitle">Inventario actual contra mínimos y máximos</p>
              </div>
              <div className="page-actions">
                <ButtonGroup>
                  <Button size="sm" color="danger" onClick={PDFTabla}>
                    PDF <i className="far fa-file-pdf"></i>
                  </Button>
                  <Button size="sm" color="primary" onClick={excel}>
                    Excel <i className="far fa-file-excel"></i>
                  </Button>
                </ButtonGroup>
              </div>
            </div>
            <div className="row">
              <div className="col-md-8">
                <Pagination
                  total={totalItems}
                  itemsPerPage={ITEMS_PER_PAGE}
                  currentPage={currentPage}
                  onPageChange={(page) => setCurrentPage(page)}
                />
              </div>
              <div className="col-md-4 d-flex flex-row-reverse">
                <Search
                  onSearch={(value) => {
                    setSearch(value);
                    setCurrentPage(1);
                  }}
                />
              </div>
            </div>
            <Row>
              <Col md={3}>
                <Label>Sección</Label>
                <Input
                  bsSize="sm"
                  type="select"
                  value={seccion}
                  onChange={(e) => {
                    setSeccion(e.target.value);
                    setCurrentPage(1);
                    setSorting({ field: "", order: "" });
                  }}
                >
                  <option value="PT">Producto Terminado</option>
                  <option value="MP">Materia Prima</option>
                </Input>
              </Col>
              <Col md={3}>
                <Label>Mostrar</Label>
                <Input
                  bsSize="sm"
                  type="select"
                  value={filtroEstado}
                  onChange={(e) => {
                    setFiltroEstado(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="bajo">Solo bajo mínimo</option>
                  <option value="">Todos</option>
                </Input>
              </Col>
            </Row>
            <br />
            <Table size="sm" striped borderless className="table-responsive-xl table-dark-header">
              <TableHeader
                headers={seccion === "PT" ? headersPT : headersMP}
                onSorting={(field, order) => setSorting({ field, order })}
              />
              <tbody>
                {seccion === "PT"
                  ? commentsData.map((a) => (
                      <tr key={a._id}>
                        <td>{a.codigo}</td>
                        <td>{a.descripcion}</td>
                        <td>{Number(a.existencia).toLocaleString("es-MX")}</td>
                        <td>{Number(a.minimo).toLocaleString("es-MX")}</td>
                        <td>{Number(a.maximo).toLocaleString("es-MX")}</td>
                        <td>{Number(a.faltante).toLocaleString("es-MX")}</td>
                        <td>{badge(a)}</td>
                      </tr>
                    ))
                  : commentsData.map((a) => (
                      <tr key={a._id}>
                        <td>{a.nombre}</td>
                        <td>{a.color}</td>
                        <td>{Number(a.existencia).toLocaleString("es-MX")}</td>
                        <td>{Number(a.minimo).toLocaleString("es-MX")}</td>
                        <td>{Number(a.faltante).toLocaleString("es-MX")}</td>
                        <td>{badge(a)}</td>
                      </tr>
                    ))}
                {commentsData.length === 0 && (
                  <tr>
                    <td colSpan={seccion === "PT" ? 7 : 6} align="center">
                      Sin resultados
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
            <div className="col-md-6">
              <Pagination
                total={totalItems}
                itemsPerPage={ITEMS_PER_PAGE}
                currentPage={currentPage}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          </div>
        ) : undefined}
      </div>
      {loader}
    </>
  );
}

export default ReporteMinimos;
